import { arrayStrictEquals } from "common/utilities/core/array";
import React from "react";
import isEqual from "react-fast-compare";
import { InView } from "react-intersection-observer";
import type { EntitySequence as Sequence, SequenceAlign } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { HandleResidueHover } from "sequenceAnalyser/components/Sequences/SequenceTable/SequenceTable";
import SequenceTableRow from "sequenceAnalyser/components/Sequences/SequenceTableRow/SequenceTableRow";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import type { SelectedColorScheme } from "sequenceAnalyser/utilities/colors";
import { getVisibleSequencesFeatures } from "sequenceAnalyser/utilities/features/features";
import { sequencesChunkHeight, sequencesChunkWidth, sequencesPerChunk } from "sequenceAnalyser/utilities/screen/screen";
import type { SequenceSearchMatch, SequencesSearch } from "sequenceAnalyser/utilities/search/search";
import type { FeatureDefinitions, SequenceAnalyserTableView } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import {
  getSequenceLabel,
  hasAnyFeatureDefinitionVisible,
} from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

interface ComponentProps {
  chunkId: number;
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  alignedSequencesChunk: SequenceAlign[];
  featureDefinitions: FeatureDefinitions;
  onResidueHover: HandleResidueHover;
  hoveredResidue: boolean;
  colors?: SelectedColorScheme;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  view: SequenceAnalyserTableView;
  zoomLevel: number;
  onDragNDrop: (from: number, to: number) => void;
  search: SequencesSearch;
  searchActiveMatch?: SequenceSearchMatch;
  alignmentVersion: number;
  hideTitle?: boolean;
  sidebarPixelWidth: number;
  expandedFeaturesForSequences: number[];
  oppositeStrands: number[];
  labelComposition: string[];
  onVisibilityChanged: (visible: boolean) => void;
  maskingResidue: string;
  /** Handle user resizing window or adjusting browser zoom level */
  windowInnerSize: string;
  alwaysVisible: boolean;
}

interface ComponentState {
  visible: boolean;
}

class SequenceTableChunk extends React.Component<ComponentProps, ComponentState> {
  public constructor(props: ComponentProps) {
    super(props);

    this.state = {
      visible: props.chunkId === 0,
    };
  }

  // Important: this component is sensitive regarding performance and should only be rerendered when needed
  // Be aware that the order of the conditions is important
  // It will only be update if:
  // - the visibility change (vertical scrolling)
  // - the residues chunk visibility change (horizontal scrolling)
  // - the feature definitions are updated (colors/visibility are changed)
  // - the view changes
  // - the label composition changes
  // - the available space changes (zoom, browser zoom, resize)
  public shouldComponentUpdate(nextProps: ComponentProps, nextState: ComponentState) {
    const {
      featureDefinitions,
      view,
      zoomLevel,
      alignedSequences,
      search,
      searchActiveMatch,
      alignmentVersion,
      hoveredResidue,
      colors,
      sidebarPixelWidth,
      expandedFeaturesForSequences,
      labelComposition,
      windowInnerSize,
      alwaysVisible,
    } = this.props;
    const hasSequencesChanged = () =>
      !arrayStrictEquals(
        alignedSequences.map((s) => s.residues),
        nextProps.alignedSequences.map((s) => s.residues),
      );

    // Visibility is the main reason for the component to update
    // Put to the top so we don't process conditions below unless it's needed
    const { visible } = this.state;
    if (nextState.visible !== visible || alwaysVisible !== nextProps.alwaysVisible) {
      return true;
    }

    // When a residue is hover and the tooltip is shown, we don't need to rerender.
    // We might be adding gaps using the space shortcut, in that case rerender.
    if (!hasSequencesChanged() && hoveredResidue) {
      return false;
    }

    // Finally, only if the component is visible, we need to check for any other change
    if (nextState.visible) {
      const hasFeatureDefinitionsChanged = () => !isEqual(featureDefinitions, nextProps.featureDefinitions);
      const hasSequencesOrderChanged = () =>
        !arrayStrictEquals(
          alignedSequences.map((s) => s.id),
          nextProps.alignedSequences.map((s) => s.id),
        );
      const hasSearchChanged = search.query !== nextProps.search.query || search.scope !== nextProps.search.scope;
      const hasSearchActiveMatchChanged = () => !isEqual(searchActiveMatch, nextProps.searchActiveMatch);
      const hasColorsChanged = () => !isEqual(colors, nextProps.colors);
      const hasSidebarPixelWidthChanged = () => !isEqual(sidebarPixelWidth, nextProps.sidebarPixelWidth);
      const hasLabelCompositionChanged = () => !arrayStrictEquals(labelComposition, nextProps.labelComposition);
      const hasExpandedFeaturesForSequencesChanged = () =>
        !isEqual(expandedFeaturesForSequences, nextProps.expandedFeaturesForSequences);

      return (
        nextProps.alignmentVersion !== alignmentVersion ||
        view !== nextProps.view ||
        zoomLevel !== nextProps.zoomLevel ||
        windowInnerSize !== nextProps.windowInnerSize ||
        hasSearchChanged ||
        hasSearchActiveMatchChanged() ||
        hasColorsChanged() ||
        hasFeatureDefinitionsChanged() ||
        hasSequencesOrderChanged() ||
        hasSidebarPixelWidthChanged() ||
        hasLabelCompositionChanged() ||
        hasExpandedFeaturesForSequencesChanged() ||
        hasSequencesChanged()
      );
    }

    return false;
  }

  componentDidUpdate(_prevProps: ComponentProps, prevState: ComponentState) {
    const { onVisibilityChanged } = this.props;

    const { visible } = this.state;

    if (prevState.visible !== visible) {
      onVisibilityChanged(visible);
    }
  }

  public render() {
    const {
      chunkId,
      sequences,
      alignedSequences,
      alignedSequencesChunk,
      featureDefinitions,
      onResidueHover,
      colors,
      columnsLetterOccurrences,
      view,
      onDragNDrop,
      search,
      searchActiveMatch,
      hideTitle,
      zoomLevel,
      sidebarPixelWidth,
      expandedFeaturesForSequences,
      oppositeStrands,
      labelComposition,
      maskingResidue,
      windowInnerSize,
      alwaysVisible,
    } = this.props;
    const { visible } = this.state;
    const hasColors = colors !== undefined;
    const hasFeatures = hasAnyFeatureDefinitionVisible(featureDefinitions);

    const visibleSequencesFeatures = getVisibleSequencesFeatures(sequences, featureDefinitions);

    return (
      // InView checks for elements been within the viewport.
      // We split sequences in chunk and only show chunks within the viewport to optimise rendering performance
      <InView
        delay={10}
        rootMargin={`${window.innerHeight / 3}px 0px ${window.innerHeight / 3}px 0px`}
        onChange={(newVisible: boolean) => {
          this.setState({ visible: newVisible });
        }}
        style={{ display: "inline-block" }}
      >
        <div
          data-chunk-rendered={visible || alwaysVisible}
          style={{
            // The height is needed to render empty chunks and get the accurate screen size
            height: `${sequencesChunkHeight(
              alignedSequencesChunk,
              hasColors,
              hasFeatures,
              visibleSequencesFeatures,
              expandedFeaturesForSequences,
              zoomLevel,
            )}px`,
            // The width is required when zooming out, otherwise it creates a bug when scrolling past the end of the table
            width:
              zoomLevel < 100
                ? "auto"
                : `${sequencesChunkWidth(alignedSequencesChunk, hideTitle !== true ? sidebarPixelWidth : 0, zoomLevel)}px`,
          }}
        >
          {(visible || alwaysVisible) &&
            alignedSequencesChunk.map((alignedSequence, chunkElementId) => {
              const sequence = sequences.find((s) => s.id === alignedSequence.id) as Sequence;
              const i = chunkId * sequencesPerChunk(hasColors, hasFeatures) + chunkElementId;

              return (
                <SequenceTableRow
                  hideTitle={hideTitle}
                  key={alignedSequence.id}
                  rowId={i}
                  name={getSequenceLabel(sequence, labelComposition)}
                  qualifiedId={sequence.qualifiedId}
                  url={sequence.entityUrl}
                  sequence={alignedSequence}
                  alignedSequences={alignedSequences}
                  features={visibleSequencesFeatures[sequence.id]}
                  featureDefinitions={featureDefinitions}
                  onResidueHover={onResidueHover}
                  colors={colors}
                  columnsLetterOccurrences={columnsLetterOccurrences}
                  view={view}
                  onDragNDrop={onDragNDrop}
                  search={search}
                  searchActiveMatch={searchActiveMatch}
                  zoomLevel={zoomLevel}
                  expandedFeaturesForSequences={expandedFeaturesForSequences}
                  oppositeStrand={oppositeStrands.includes(alignedSequence.id)}
                  maskingResidue={maskingResidue}
                  windowInnerSize={windowInnerSize}
                />
              );
            })}
        </div>
      </InView>
    );
  }
}

export default SequenceTableChunk;
